import {
  deleteRequest,
  postRequest,
  putFormRequest,
  putRequest,
} from 'api/fetcher'
import {
  Place,
  PlacePreference,
  ResponseEnvelope,
  UnreportedPlace,
} from 'infra/type'
import { CURRENT_OS, OS } from 'infra/constant'

export const authenticate = async (token: string) => {
  const res = (await postRequest('/users/authenticate', {
    kakao_token: token,
  })) as ResponseEnvelope<{ token: string }>
  return res.data?.token as string
}

export const authenticateApple = async (token: string) => {
  const res = (await postRequest('/users/authenticate/apple', {
    apple_token: token,
  })) as ResponseEnvelope<{ token: string }>
  return res.data?.token as string
}

export const authenticateReview = async (
  username: string,
  password: string,
) => {
  const res = (await postRequest('/users/authenticate/review', {
    username,
    password,
  })) as ResponseEnvelope<{ token: string }>
  return res.data?.token as string
}

// Web-only: Kakao JS SDK can only return an authorization code (client-side
// access_token login is deprecated). The backend exchanges the code for a Kakao
// access_token (REST key) and runs the same auth as /users/authenticate.
// Backend: POST /users/authenticate/kakao/web (gogo-api-v2, added).
export const authenticateKakaoWeb = async (
  code: string,
  redirectUri: string,
) => {
  const res = (await postRequest('/users/authenticate/kakao/web', {
    code,
    redirect_uri: redirectUri,
  })) as ResponseEnvelope<{ token: string }>
  return res.data?.token as string
}

export const reportUnknownPlace = async (
  p: UnreportedPlace,
  categories: number[],
) => {
  return postRequest('/places/report', {
    ...p,
    coord: {
      lat: String(p.coord.lat),
      lng: String(p.coord.lng),
    },
    categories,
  }) as Promise<ResponseEnvelope<{ placeId: number }>>
}

export const editPlace = async (p: Place, categories: number[]) => {
  return putRequest(`/places/${p.id}/categories`, {
    categories,
  }) as Promise<ResponseEnvelope<{}>>
}

export const setPreference = async (
  placeId: number,
  value: PlacePreference,
) => {
  return putRequest(`/places/${placeId}/preference`, { value })
}

export const deleteAccount = async () => {
  return deleteRequest('/users/me')
}

export const feedback = async (value: string) => {
  return postRequest('/feedback', { value })
}

export const deletePlace = async (placeId: number) => {
  return deleteRequest(`/places/${placeId}`)
}

export const requestDeletePlace = async (placeId: number) => {
  return postRequest(`/places/${placeId}/request-delete`)
}

export const toggleFollowUser = async (
  userId: number,
  isFollowing: boolean,
) => {
  return putRequest(`/users/${userId}/follow`, { value: isFollowing })
}

export interface UpdateProfileParams {
  nickname?: string
  bio?: string
  instagramId?: string
  image?: { uri: string; name: string; type: string }
}

export const updateUserProfile = async (params: UpdateProfileParams) => {
  const formData = new FormData()
  const data: Pick<UpdateProfileParams, 'nickname' | 'bio' | 'instagramId'> = {}
  if (params.nickname !== undefined) {
    data.nickname = params.nickname
  }
  if (params.bio !== undefined) {
    data.bio = params.bio
  }
  if (params.instagramId !== undefined) {
    data.instagramId = params.instagramId
  }
  formData.append('data', JSON.stringify(data))
  if (params.image) {
    if (CURRENT_OS === OS.WEB) {
      // Web: the picked uri is an object URL (blob:); fetch it into a Blob so the
      // multipart body carries real bytes. RN's {uri,name,type} file descriptor
      // in FormData is native-only and would serialize to "[object Object]".
      const blob = await fetch(params.image.uri).then((r) => r.blob())
      formData.append('file', blob, params.image.name || 'image.jpg')
    } else {
      formData.append('file', params.image as any)
    }
  }
  return putFormRequest('/users/me', formData)
}
